import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FaChartLine,
  FaShieldAlt,
  FaTelegramPlane,
  FaTiktok,
} from "react-icons/fa";
import { FaBoltLightning } from "react-icons/fa6";
import {
  collection,
  doc,
  getDocs,
  query,
  setDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebase";

const LEGACY_APP_ID = "86059";
const DERIV_CLIENT_ID = "33xwXCIzDugoKHqIRFO5j";
const DERIV_REDIRECT_URL = "https://www.matchesguru.site/";
const DERIV_OAUTH_URL = "https://auth.deriv.com/oauth2/auth";
const DERIV_TOKEN_URL = "https://auth.deriv.com/oauth2/token";
const DERIV_OPTIONS_ACCOUNTS_URL =
  "https://api.derivws.com/trading/v1/options/accounts";
const DERIV_OAUTH_SCOPE = "trade";
const DERIV_WS_URL = `wss://ws.binaryws.com/websockets/v3?app_id=${LEGACY_APP_ID}`;
const DERIV_PKCE_VERIFIER_KEY = "derivPkceVerifier";
const DERIV_OAUTH_STATE_KEY = "derivOAuthState";

const socialLinks = [
  {
    label: "Telegram",
    href: "https://t.me/+DZqYd1cQD4VjMWI0",
    icon: FaTelegramPlane,
  },
  {
    label: "TikTok",
    href: "https://www.tiktok.com/@derivguru?_r=1&_t=ZS-948jHjpbKho",
    icon: FaTiktok,
  },
];

const normalizeEmail = (email) => email?.trim().toLowerCase() || "";

const firstNonEmptyString = (...values) =>
  values.find(
    (value) => typeof value === "string" && value.trim().length > 0
  ) || "";

const base64UrlEncode = (buffer) =>
  btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/g, "");

const createRandomString = (byteLength = 32) => {
  const randomValues = new Uint8Array(byteLength);
  window.crypto.getRandomValues(randomValues);
  return base64UrlEncode(randomValues.buffer);
};

const createPkceChallenge = async (verifier) => {
  const digest = await window.crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(verifier)
  );
  return base64UrlEncode(digest);
};

const hasOAuthCallbackParams = (params) =>
  params.has("acct1") ||
  params.has("token1") ||
  params.has("code") ||
  params.has("error");

const buildCallbackError = (params) => {
  const error = params.get("error");
  if (!error) return null;
  const description = params.get("error_description");
  return new Error(description || error || "Deriv authentication was cancelled");
};

const exchangeAuthorizationCode = async (code, verifier) => {
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: DERIV_CLIENT_ID,
    code,
    code_verifier: verifier,
    redirect_uri: DERIV_REDIRECT_URL,
  });

  const response = await fetch(DERIV_TOKEN_URL, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      payload.error_description ||
        payload.error ||
        "Failed to exchange Deriv authorization code"
    );
  }

  if (!payload.access_token) {
    throw new Error("Deriv token response did not include an access token");
  }

  return payload.access_token;
};

const startDerivOAuth = async ({ prompt } = {}) => {
  const verifier = createRandomString(48);
  const state = createRandomString(24);
  const challenge = await createPkceChallenge(verifier);

  sessionStorage.setItem(DERIV_PKCE_VERIFIER_KEY, verifier);
  sessionStorage.setItem(DERIV_OAUTH_STATE_KEY, state);

  const params = new URLSearchParams({
    response_type: "code",
    client_id: DERIV_CLIENT_ID,
    redirect_uri: DERIV_REDIRECT_URL,
    scope: DERIV_OAUTH_SCOPE,
    state,
    code_challenge: challenge,
    code_challenge_method: "S256",
    app_id: LEGACY_APP_ID,
  });

  if (prompt) params.set("prompt", prompt);

  window.location.href = `${DERIV_OAUTH_URL}?${params.toString()}`;
};

const fetchDerivProfile = (token) =>
  new Promise((resolve, reject) => {
    if (!token) { resolve(null); return; }

    const ws = new WebSocket(DERIV_WS_URL);
    const timeout = window.setTimeout(() => {
      try { ws.close(); } catch {}
      reject(new Error("Timed out while fetching Deriv profile"));
    }, 10000);

    ws.onopen = () => ws.send(JSON.stringify({ authorize: token }));

    ws.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);
        if (payload.error) {
          clearTimeout(timeout);
          ws.close();
          reject(new Error(payload.error.message || "Deriv authorization failed"));
          return;
        }
        if (!payload.authorize) return;
        const { authorize } = payload;
        clearTimeout(timeout);
        ws.close();
        resolve({
          loginid: authorize.loginid || authorize.login_id || "",
          email: authorize.email || authorize.email_address || "",
          name:
            authorize.fullname ||
            authorize.full_name ||
            authorize.name ||
            authorize.email ||
            authorize.loginid ||
            "",
          accountList: Array.isArray(authorize.account_list)
            ? authorize.account_list
            : [],
          rawAuthorize: authorize,
        });
      } catch (error) {
        clearTimeout(timeout);
        ws.close();
        reject(error);
      }
    };

    ws.onerror = () => {
      clearTimeout(timeout);
      try { ws.close(); } catch {}
      reject(new Error("Failed to connect to Deriv"));
    };
  });

const fetchDerivOptionsAccounts = async (token) => {
  if (!token) return [];

  const response = await fetch(DERIV_OPTIONS_ACCOUNTS_URL, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Deriv-App-ID": DERIV_CLIENT_ID,
    },
  });

  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(
      payload?.errors?.[0]?.message ||
        payload?.message ||
        "Failed to fetch Deriv trading accounts"
    );
  }

  if (Array.isArray(payload?.data)) return payload.data;
  if (payload?.data && typeof payload.data === "object") return [payload.data];
  return [];
};

const resolveDerivIdentity = ({
  fallbackLoginid = "",
  profile = null,
  optionAccounts = [],
} = {}) => {
  const accountList = Array.isArray(profile?.accountList)
    ? profile.accountList
    : [];
  const matchingAccount =
    fallbackLoginid &&
    accountList.find((account) =>
      [
        account?.loginid,
        account?.login_id,
        account?.account,
        account?.account_id,
        account?.accountId,
      ].includes(fallbackLoginid)
    );
  const firstAccount = matchingAccount || accountList[0] || null;
  const matchingOptionAccount =
    fallbackLoginid &&
    optionAccounts.find((account) =>
      [account?.account_id, account?.accountId].includes(fallbackLoginid)
    );
  const firstOptionAccount = matchingOptionAccount || optionAccounts[0] || null;

  const loginid = firstNonEmptyString(
    fallbackLoginid,
    profile?.loginid,
    profile?.rawAuthorize?.loginid,
    profile?.rawAuthorize?.login_id,
    matchingAccount?.loginid,
    matchingAccount?.login_id,
    matchingAccount?.account,
    matchingAccount?.account_id,
    matchingAccount?.accountId,
    firstAccount?.loginid,
    firstAccount?.login_id,
    firstAccount?.account,
    firstAccount?.account_id,
    firstAccount?.accountId,
    profile?.rawAuthorize?.account,
    profile?.rawAuthorize?.account_id,
    profile?.rawAuthorize?.accountId,
    matchingOptionAccount?.account_id,
    matchingOptionAccount?.accountId,
    firstOptionAccount?.account_id,
    firstOptionAccount?.accountId
  );

  const rawEmail = firstNonEmptyString(
    profile?.email,
    profile?.rawAuthorize?.email,
    profile?.rawAuthorize?.email_address
  );

  const name = firstNonEmptyString(
    profile?.name,
    profile?.rawAuthorize?.fullname,
    rawEmail,
    loginid
  );

  return { loginid, rawEmail, email: normalizeEmail(rawEmail), name };
};

const findExistingUserDoc = async (usersCol, loginid, email) => {
  if (loginid) {
    const loginSnapshot = await getDocs(
      query(usersCol, where("loginid", "==", loginid))
    );
    if (!loginSnapshot.empty) return loginSnapshot.docs[0];
  }

  const normalizedEmail = normalizeEmail(email);
  const emailCandidates = Array.from(
    new Set([email, normalizedEmail].filter(Boolean))
  );

  if (!emailCandidates.length) return null;

  for (const emailCandidate of emailCandidates) {
    const emailSnapshot = await getDocs(
      query(usersCol, where("email", "==", emailCandidate))
    );
    if (!emailSnapshot.empty) return emailSnapshot.docs[0];
  }

  return null;
};

export default function LoginDeriv() {
  const navigate = useNavigate();
  const location = useLocation();
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [authError, setAuthError] = useState("");

  useEffect(() => {
    const handleOAuth = async () => {
      const params = new URLSearchParams(location.search);
      if (!hasOAuthCallbackParams(params)) return;

      setAuthError("");

      try {
        const callbackError = buildCallbackError(params);
        if (callbackError) throw callbackError;

        const isLegacyCallback = params.has("token1") || params.has("acct1");
        let loginid = params.get("acct1") || "";
        let token = params.get("token1") || "";

        if (params.has("code")) {
          const returnedState = params.get("state");
          const storedState = sessionStorage.getItem(DERIV_OAUTH_STATE_KEY);
          const verifier = sessionStorage.getItem(DERIV_PKCE_VERIFIER_KEY);

          if (!storedState || !verifier) {
            throw new Error("Deriv login session expired. Please try again.");
          }
          if (!returnedState || returnedState !== storedState) {
            throw new Error("Deriv login validation failed. Please try again.");
          }

          token = await exchangeAuthorizationCode(params.get("code"), verifier);
          sessionStorage.removeItem(DERIV_OAUTH_STATE_KEY);
          sessionStorage.removeItem(DERIV_PKCE_VERIFIER_KEY);
        }

        if (!loginid && !token) {
          throw new Error("Deriv did not return a valid login session.");
        }

        const usersCol = collection(db, "deriv_users");

        let profile = null;
        let optionAccounts = [];

        if (token && isLegacyCallback) {
          try {
            profile = await fetchDerivProfile(token);
          } catch (profileError) {
            console.warn("[LoginDeriv] Could not fetch full Deriv profile:", profileError);
          }
        }

        if (token) {
          try {
            optionAccounts = await fetchDerivOptionsAccounts(token);
          } catch (accountsError) {
            console.warn("[LoginDeriv] Could not fetch Deriv options accounts:", accountsError);
          }
        }

        const identity = resolveDerivIdentity({ fallbackLoginid: loginid, profile, optionAccounts });
        loginid = identity.loginid;
        if (!loginid) throw new Error("Unable to resolve your Deriv account ID.");

        const resolvedEmail = identity.email || "";
        const resolvedName = identity.rawEmail
          ? identity.name?.trim() || ""
          : profile?.name?.trim() || "";

        const existingDoc = await findExistingUserDoc(
          usersCol,
          loginid,
          identity.rawEmail || resolvedEmail
        );
        const existingData = existingDoc?.data() || {};
        const timestamp = new Date().toISOString();

        if (existingDoc) {
          await setDoc(
            existingDoc.ref,
            {
              loginid,
              email: resolvedEmail || existingData.email || "",
              name: resolvedName || existingData.name || "",
              lastLoginAt: timestamp,
            },
            { merge: true }
          );
        } else {
          await setDoc(doc(usersCol, loginid), {
            loginid,
            email: resolvedEmail,
            name: resolvedName,
            status: "pending",
            approved: false,
            createdAt: timestamp,
            lastLoginAt: timestamp,
          });
        }

        localStorage.setItem("currentDerivLogin", loginid);

        const effectiveEmail = resolvedEmail || existingData.email || "";
        const effectiveName = resolvedName || existingData.name || "";
        const approved = existingData.approved ?? false;
        const userDocId = existingDoc?.id || loginid;

        if (!effectiveEmail || !effectiveName) {
          navigate("/profile-setup", {
            replace: true,
            state: {
              userDocId,
              redirectTo: approved ? "/user" : "/pending",
              prefillName: effectiveName,
              prefillEmail: effectiveEmail,
            },
          });
          return;
        }

        navigate(approved ? "/user" : "/pending", { replace: true });
      } catch (err) {
        console.error("[LoginDeriv] OAuth flow failed:", err);
        sessionStorage.removeItem(DERIV_OAUTH_STATE_KEY);
        sessionStorage.removeItem(DERIV_PKCE_VERIFIER_KEY);
        setAuthError(err.message || "Unable to complete Deriv sign in.");
        setCheckingAuth(false);
      }
    };

    handleOAuth();
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    const checkSession = async () => {
      const sessionLoginid = localStorage.getItem("currentDerivLogin");
      if (!sessionLoginid) { setCheckingAuth(false); return; }

      try {
        const usersCol = collection(db, "deriv_users");
        const q = query(usersCol, where("loginid", "==", sessionLoginid));
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) { setCheckingAuth(false); return; }

        const data = querySnapshot.docs[0].data();
        if (!data.email || !data.name) {
          navigate("/profile-setup", { replace: true, state: { redirectTo: data.approved ? "/user" : "/pending" } });
          return;
        }
        navigate(data.approved ? "/user" : "/pending", { replace: true });
      } catch (err) {
        console.error("[LoginDeriv] Auth check failed:", err);
        setCheckingAuth(false);
      }
    };

    const params = new URLSearchParams(location.search);
    if (!hasOAuthCallbackParams(params)) checkSession();
  }, [location.search, navigate]);

  const handleDerivLogin = async () => {
    try {
      setAuthError("");
      await startDerivOAuth();
    } catch (error) {
      console.error("[LoginDeriv] Failed to start Deriv login:", error);
      setAuthError("Unable to start Deriv sign in right now.");
    }
  };

  const handleDerivSignup = async () => {
    try {
      setAuthError("");
      await startDerivOAuth({ prompt: "registration" });
    } catch (error) {
      console.error("[LoginDeriv] Failed to start Deriv signup:", error);
      setAuthError("Unable to start Deriv account creation right now.");
    }
  };

  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#020617] via-[#050b18] to-[#020617] flex items-center justify-center text-gray-400">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-full border-4 border-cyan-700/40 border-t-cyan-400 animate-spin"></div>
          <p>Consulting the Guru...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#020617] via-[#050b18] to-[#020617] text-white relative overflow-hidden flex flex-col">

      <div className="absolute inset-0 opacity-20 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_40%,#06b6d4_0%,transparent_55%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_60%,#0ea5e9_0%,transparent_55%)]"></div>
      </div>

      <nav className="sticky top-0 bg-[#020617]/90 backdrop-blur-lg border-b border-cyan-900/40 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex justify-between items-center">
          <span className="text-xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 via-blue-400 to-cyan-500">
            MatchesGuru
          </span>
          <Link to="/" className="text-cyan-400 hover:text-cyan-300 transition text-sm">
            ← Home
          </Link>
        </div>
      </nav>

      <div className="flex-1 flex items-center justify-center px-6 py-12 relative">
        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl w-full">

          {/* Left */}
          <div className="flex flex-col justify-center space-y-8">
            <p className="inline-flex rounded-full border border-cyan-700/40 bg-cyan-700/10 px-4 py-2 text-xs font-semibold uppercase tracking-widest text-cyan-400 w-fit">
              MatchesGuru Access
            </p>
            <h1 className="text-4xl md:text-5xl font-black leading-tight">
              Sign in with Deriv to enter the{" "}
              <span className="text-cyan-400">MatchesGuru</span> dashboard.
            </h1>
            <p className="text-base md:text-lg text-gray-300 max-w-xl">
              Existing accounts sign in directly. New users create a Deriv account
              first, then continue into the approval flow automatically.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Feature
                icon={<FaChartLine />}
                title="Matches-first signals"
                text="Focused on a dedicated MatchesGuru experience."
              />
              <Feature
                icon={<FaBoltLightning />}
                title="Live timing windows"
                text="Use confidence and entry timing to approach setups with structure."
              />
              <Feature
                icon={<FaShieldAlt />}
                title="OAuth security"
                text="No password storage. Auth happens through Deriv directly."
              />
            </div>

            <div className="rounded-2xl border border-cyan-900/40 bg-white/5 p-6">
              <p className="text-sm font-semibold uppercase tracking-widest text-cyan-400">
                Community links
              </p>
              <div className="mt-4 flex flex-wrap gap-3">
                {socialLinks.map(({ label, href, icon: Icon }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-full border border-cyan-900/40 px-4 py-2 text-sm text-gray-300 transition hover:border-cyan-500/50 hover:text-cyan-400"
                  >
                    <Icon />
                    {label}
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Right login card */}
          <div className="bg-gradient-to-b from-[#0a1325] to-[#050814] rounded-3xl border border-cyan-800/50 p-10 shadow-2xl shadow-cyan-950/40 flex flex-col justify-center">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-3 h-3 rounded-full bg-cyan-500 animate-pulse"></div>
              <h2 className="text-2xl font-bold text-cyan-300">Enter the Dojo</h2>
            </div>

            <div className="rounded-2xl border border-cyan-900/40 bg-[#020617]/60 p-5">
              <p className="text-sm font-semibold uppercase tracking-widest text-cyan-400">
                What happens next
              </p>
              <div className="mt-4 space-y-3 text-sm text-gray-300">
                <p>1. Sign in with your existing Deriv account.</p>
                <p>2. Or create a new Deriv account if you don't have one.</p>
                <p>3. Access the dashboard once your account is approved.</p>
              </div>
            </div>

            {authError && (
              <div className="mt-5 rounded-xl border border-red-700/40 bg-red-900/20 px-4 py-3 text-sm text-red-300">
                {authError}
              </div>
            )}

            <button
              onClick={handleDerivLogin}
              className="mt-8 w-full rounded-full bg-gradient-to-r from-cyan-600 via-blue-500 to-cyan-500 hover:from-cyan-500 hover:to-blue-400 py-4 text-lg font-bold shadow-lg shadow-cyan-900/40 transition"
            >
              Login with Deriv
            </button>

            <button
              onClick={handleDerivSignup}
              className="mt-4 w-full rounded-full border border-cyan-800/50 bg-transparent py-4 text-base font-semibold text-gray-200 transition hover:border-cyan-500/50 hover:text-cyan-400"
            >
              Create a new Deriv account
            </button>

            <p className="mt-5 text-center text-sm text-gray-400">
              Access is granted after approval in the admin dashboard.
            </p>

            <div className="mt-6 border-t border-cyan-900/40 pt-6 text-center">
              <Link
                to="/"
                className="text-sm font-semibold text-cyan-400 transition hover:text-cyan-300"
              >
                ← Return to main site
              </Link>
            </div>
          </div>
        </div>
      </div>

      <footer className="py-8 text-center text-gray-500 text-sm border-t border-cyan-950/30">
        <p>© 2026 MatchesGuru • All rights reserved</p>
      </footer>
    </div>
  );
}

function Feature({ icon, title, text }) {
  return (
    <div className="rounded-2xl border border-cyan-900/40 bg-white/5 p-4">
      <div className="text-lg text-cyan-400">{icon}</div>
      <h4 className="mt-3 font-semibold text-white">{title}</h4>
      <p className="mt-2 text-sm leading-6 text-gray-400">{text}</p>
    </div>
  );
}
